import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Sparkles, Heart, Users, Baby, Home, Coins } from 'lucide-react';
import { useMatching } from '@/hooks/useMatching';
import { useCredits } from '@/hooks/useCredits';
import { usePets } from '@/hooks/usePets';
import { useAuth } from '@/contexts/AuthContext';
import { useTrialStatus } from '@/hooks/useTrialStatus';
import { Skeleton } from '@/components/ui/skeleton';
import { PurchaseCreditsModal } from './PurchaseCreditsModal';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from 'react-i18next';
import { translateSpeciesSingular } from '@/utils/speciesTranslation';

interface RequestMatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetPetId: string;
  targetPetName: string;
  targetPetSpecies?: string;
}

export const RequestMatchModal = ({ isOpen, onClose, targetPetId, targetPetName, targetPetSpecies }: RequestMatchModalProps) => {
  const { t } = useTranslation();
  
  const MATCH_TYPE_OPTIONS = [
    { value: 'playdate', label: t('matchTypes.playdate'), icon: Heart, cost: 1 },
    { value: 'friendship', label: t('matchTypes.friendship'), icon: Users, cost: 1 },
    { value: 'breeding', label: t('matchTypes.breeding'), icon: Baby, cost: 4 },
    { value: 'adoption', label: t('matchTypes.adoption'), icon: Home, cost: 2 },
  ];

  const { user } = useAuth();
  const { pets, isLoading: loadingPets } = usePets();
  const { createMatchRequest } = useMatching();
  const { balance, hasEnoughCredits, getMatchCost } = useCredits();
  const { isInTrial, daysRemaining } = useTrialStatus();
  
  const [selectedPetId, setSelectedPetId] = useState('');
  const [matchType, setMatchType] = useState<'playdate' | 'breeding' | 'friendship' | 'adoption'>('playdate');
  const [message, setMessage] = useState('');
  const [isSuperMatch, setIsSuperMatch] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showCompatibility, setShowCompatibility] = useState(false);
  const [compatibility, setCompatibility] = useState<any>(null);
  const [calculatingScore, setCalculatingScore] = useState(false);

  // Filtrar mascotas del usuario por especie compatible
  const allUserPets = pets?.filter(p => p.user_id === user?.id) || [];
  const userPets = targetPetSpecies 
    ? allUserPets.filter(p => p.species === targetPetSpecies)
    : allUserPets;
  
  const requiredCredits = getMatchCost(matchType, isSuperMatch);
  const canSend = hasEnoughCredits(requiredCredits);

  // Auto-select first pet and auto-calculate compatibility
  useEffect(() => {
    if (userPets.length > 0 && !selectedPetId) {
      setSelectedPetId(userPets[0].id);
    }
  }, [userPets, selectedPetId]);

  // Auto-calculate compatibility when pet or match type changes (FREE)
  useEffect(() => {
    if (selectedPetId && targetPetId && isOpen) {
      handleCalculateCompatibility();
    }
  }, [selectedPetId, matchType, isOpen]);

  const handleCalculateCompatibility = async () => {
    if (!selectedPetId) return;

    setCalculatingScore(true);
    try {
      const { data, error } = await supabase.functions.invoke('calculate-compatibility-score', {
        body: {
          requester_pet_id: selectedPetId,
          target_pet_id: targetPetId,
          match_type: matchType
        }
      });

      if (error) throw error;

      setCompatibility(data);
    } catch (error: any) {
      console.error('Error calculating compatibility:', error);
    } finally {
      setCalculatingScore(false);
    }
  };

  const handleSendRequest = async () => {
    if (!selectedPetId || !canSend) return;
    
    try {
      await createMatchRequest.mutateAsync({
        requester_pet_id: selectedPetId,
        target_pet_id: targetPetId,
        match_type: matchType,
        message: message || undefined,
        is_super_match: isSuperMatch
      });
      
      onClose();
      setMessage('');
      setIsSuperMatch(false);
      setCompatibility(null);
    } catch (error) {
      console.error('Error sending match request:', error);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="h-5 w-5 text-pink-500" />
              {t('matchModal.title', { petName: targetPetName })}
            </DialogTitle>
            <DialogDescription>
              {t('matchModal.title', { petName: targetPetName })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Unified Credit Balance (includes trial info if applicable) */}
            <Alert className={isInTrial 
              ? "bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200" 
              : "bg-blue-50 border-blue-200"
            }>
              <Coins className={`h-4 w-4 ${isInTrial ? 'text-purple-600' : 'text-blue-600'}`} />
              <AlertDescription className="flex items-center justify-between">
                <span className="text-sm">
                  {isInTrial ? (
                    <>
                      🎉 <strong>{balance}</strong> {t('matchModal.credits')}
                      <span className="text-purple-600 ml-1">
                        ({t('matchModal.trialBonus')} • {daysRemaining} {t('matchModal.daysRemaining')})
                      </span>
                    </>
                  ) : (
                    <>{balance} {t('matchModal.credits')}</>
                  )}
                </span>
                <Button 
                  variant="link" 
                  size="sm" 
                  onClick={() => setShowCreditsModal(true)}
                  className={`h-auto p-0 ${isInTrial ? 'text-purple-600' : 'text-blue-600'}`}
                >
                  {t('credits.buyMore')}
                </Button>
              </AlertDescription>
            </Alert>

            {/* Select Your Pet */}
            <div className="space-y-2">
              <Label htmlFor="pet" className="text-base font-semibold">
                {t('matchModal.selectPet')}
              </Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                {t('matchModal.selectPetPlaceholder')}
              </p>
              {loadingPets ? (
                <Skeleton className="h-10 w-full" />
              ) : userPets.length === 0 ? (
                <Alert className="bg-amber-50 border-amber-200">
                  <AlertDescription className="text-amber-800">
                    {targetPetSpecies 
                      ? t('matchModal.noPetsForSpecies', { species: translateSpeciesSingular(targetPetSpecies, t) })
                      : t('matchModal.noPetsAvailable')}
                  </AlertDescription>
                </Alert>
              ) : (
                <Select value={selectedPetId} onValueChange={setSelectedPetId}>
                  <SelectTrigger id="pet">
                    <SelectValue placeholder={t('matchModal.selectPetPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {userPets.map(pet => (
                      <SelectItem key={pet.id} value={pet.id}>
                        {pet.name} {pet.species ? `(${translateSpeciesSingular(pet.species, t)})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Match Type */}
            <div className="space-y-2">
              <Label htmlFor="match-type">{t('matchModal.matchType')}</Label>
              <Select value={matchType} onValueChange={(v: any) => setMatchType(v)}>
                <SelectTrigger id="match-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MATCH_TYPE_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center justify-between w-full">
                        <span>{option.label}</span>
                        <span className="text-xs text-muted-foreground ml-4">
                          {t('matchModal.requiredCredits', { credits: option.cost, s: option.cost > 1 ? 's' : '' })}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="message">{t('matchModal.message')}</Label>
              <Textarea
                id="message"
                placeholder={t('matchModal.messagePlaceholder')}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={500}
                rows={3}
              />
              <p className="text-xs text-muted-foreground text-right">
                {message.length}/500
              </p>
            </div>

            {/* SuperMatch™ Option */}
            <div className="flex items-center space-x-2 p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
              <input
                type="checkbox"
                id="super-match"
                checked={isSuperMatch}
                onChange={(e) => setIsSuperMatch(e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="super-match" className="flex items-center gap-2 cursor-pointer flex-1">
                <Sparkles className="h-4 w-4 text-purple-600" />
                <span className="text-sm">
                  {t('matchModal.superMatch')} (+5 {t('matchModal.credits')})
                </span>
              </Label>
            </div>

            {/* Compatibility Score Display (Auto-calculated, FREE) */}
            {compatibility && !calculatingScore && (
              <Alert className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-300">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <div className="text-3xl font-bold text-green-700">
                        {compatibility.compatibility_score}%
                      </div>
                      <Sparkles className="h-5 w-5 text-green-600" />
                    </div>
                    <div className="text-sm text-green-600 font-medium">
                      {compatibility.compatibility_score >= 75 
                        ? t('requestMatch.compatibility.greatMatch')
                        : compatibility.compatibility_score >= 50 
                        ? t('requestMatch.compatibility.goodMatch')
                        : t('requestMatch.compatibility.couldWork')}
                    </div>
                  </div>
                  <div className="space-y-1">
                    {compatibility.reasons?.slice(0, 3).map((reason: string, i: number) => (
                      <div key={i} className="text-xs text-green-700 flex items-start gap-1">
                        <span>•</span>
                        <span>{reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Alert>
            )}

            {calculatingScore && (
              <Alert className="bg-blue-50 border-blue-200">
                <AlertDescription className="flex items-center gap-2 text-sm">
                  <div className="animate-spin">⚙️</div>
                  <span>{t('requestMatch.compatibility.calculating')}</span>
                </AlertDescription>
              </Alert>
            )}

            {/* Cost Summary */}
            <Alert className={canSend ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
              <AlertDescription className="text-sm">
                {canSend ? (
                  <>
                    {t('matchModal.cost')} {t('matchModal.requiredCredits', { credits: requiredCredits, s: requiredCredits > 1 ? 's' : '' })}
                    {' • '}
                    {t('matchModal.after')} {balance - requiredCredits} {t('matchModal.credits')}
                    {isInTrial && <span className="text-purple-600 font-medium ml-1">({t('matchModal.trialCreditsNote')})</span>}
                  </>
                ) : (
                  <span className="text-red-700">
                    {t('matchModal.insufficientCredits', { required: requiredCredits, balance })}
                  </span>
                )}
              </AlertDescription>
            </Alert>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">
              {t('matchModal.cancel')}
            </Button>
              <Button
                onClick={handleSendRequest}
                disabled={!selectedPetId || !canSend || createMatchRequest.isPending || loadingPets}
                className="flex-1 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700"
              >
                {createMatchRequest.isPending 
                  ? t('requestMatch.sending') 
                  : `${t('matchModal.sendRequest')} (${requiredCredits} 💎)`}
              </Button>
          </div>
        </DialogContent>
      </Dialog>

      <PurchaseCreditsModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
      />
    </>
  );
};
